<script lang="ts" setup>
import type { DnsRecord } from '@/types'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { useDnsApiBase } from '@/composables/useDnsApiBase'
import { handleAxiosError } from '@/composables/useAxiosError'
import { bulkDnsRefusals } from '@/composables/dnsBulkRefusals'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { DNS_TTL_PRESETS } from '@/constants/dnsPresets'

const props = defineProps<{
  selectedCount: number
  selectedRecords: DnsRecord[]
  domainId: number
  apiBase?: string
}>()

const apiBase = useDnsApiBase(() => props.domainId, { override: () => props.apiBase })

const emit = defineEmits<{
  (e: 'clearSelection'): void
  (e: 'recordsDeleted', ids: number[]): void
  (e: 'recordsUpdated', payload: { ids: number[]; ttl: number }): void
}>()

const notifyStore = useNotifyStore()
const { t } = useI18n()

// State
const showDeleteConfirm = ref(false)
const showTtlEdit = ref(false)
const deleting = ref(false)
const updating = ref(false)
const deleteProgress = ref({ current: 0, total: 0 })
const updateProgress = ref({ current: 0, total: 0 })
const selectedTtl = ref<number>(3600)

const progressPercent = computed(() => {
  if (deleteProgress.value.total === 0) return 0
  return Math.round((deleteProgress.value.current / deleteProgress.value.total) * 100)
})

const updateProgressPercent = computed(() => {
  if (updateProgress.value.total === 0) return 0
  return Math.round((updateProgress.value.current / updateProgress.value.total) * 100)
})

const confirmDelete = () => {
  showDeleteConfirm.value = true
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
}

const executeDelete = async () => {
  deleting.value = true
  deleteProgress.value = { current: 0, total: props.selectedRecords.length }

  let successCount = 0
  let failCount = 0
  const deletedIds: number[] = []
  const refusals = bulkDnsRefusals()

  for (const record of props.selectedRecords) {
    try {
      await useAxios('DELETE', `${apiBase.value}/records/${record.id}`, true)
      successCount++
      deletedIds.push(record.id)
    } catch (error) {
      failCount++
      handleAxiosError(error, { logPrefix: 'DnsBulkActions:delete' })
      refusals.note(error, record)
    }
    deleteProgress.value.current++
  }

  deleting.value = false
  showDeleteConfirm.value = false
  refusals.notify(t)

  if (successCount > 0) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: failCount > 0 ? 'warning' : 'success',
      title: t('dashboard.domains.dns.bulkDeletedTitle'),
      message: failCount > 0
        ? t('dashboard.domains.dns.bulkDeletedPartial', { success: successCount, fail: failCount })
        : t('dashboard.domains.dns.bulkDeletedSuccess', { count: successCount })
    })
  }

  emit('clearSelection')
  emit('recordsDeleted', deletedIds)
}

const openTtlEdit = () => {
  // Default to most common TTL among selected records
  const ttlCounts = new Map<number, number>()
  for (const record of props.selectedRecords) {
    if (record.ttl) {
      ttlCounts.set(record.ttl, (ttlCounts.get(record.ttl) || 0) + 1)
    }
  }
  let mostCommon = 3600
  let maxCount = 0
  for (const [ttl, count] of ttlCounts) {
    if (count > maxCount) {
      mostCommon = ttl
      maxCount = count
    }
  }
  selectedTtl.value = mostCommon
  showTtlEdit.value = true
}

const cancelTtlEdit = () => {
  showTtlEdit.value = false
}

const executeTtlUpdate = async () => {
  updating.value = true
  updateProgress.value = { current: 0, total: props.selectedRecords.length }

  let successCount = 0
  let failCount = 0
  const updatedIds: number[] = []
  const refusals = bulkDnsRefusals()

  for (const record of props.selectedRecords) {
    try {
      await useAxios('PUT', `${apiBase.value}/records/${record.id}`, true, {
        name: record.name,
        type: record.type,
        content: record.content,
        ttl: selectedTtl.value,
        priority: record.priority
      })
      successCount++
      updatedIds.push(record.id)
    } catch (error) {
      failCount++
      handleAxiosError(error, { logPrefix: 'DnsBulkActions:updateTtl' })
      refusals.note(error, record)
    }
    updateProgress.value.current++
  }

  updating.value = false
  showTtlEdit.value = false
  refusals.notify(t)

  if (successCount > 0) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: failCount > 0 ? 'warning' : 'success',
      title: t('dashboard.domains.dns.bulkTtlUpdatedTitle'),
      message: failCount > 0
        ? t('dashboard.domains.dns.bulkTtlUpdatedPartial', { success: successCount, fail: failCount })
        : t('dashboard.domains.dns.bulkTtlUpdatedSuccess', { count: successCount, ttl: formatTtlLabel(selectedTtl.value) })
    })
  }

  emit('clearSelection')
  emit('recordsUpdated', { ids: updatedIds, ttl: selectedTtl.value })
}

const formatTtlLabel = (ttl: number): string => {
  const preset = DNS_TTL_PRESETS.find((p) => p.value === ttl)
  return preset ? t(preset.labelKey) : t('dashboard.domains.dns.ttlPresets.seconds', { value: ttl })
}

const translatedTtlPresets = computed(() =>
  DNS_TTL_PRESETS.map(p => ({ value: p.value, label: t(p.labelKey) }))
)
</script>

<template>
  <Teleport to="body">
    <!-- Floating Action Toolbar -->
    <Transition name="slide-up">
      <div v-if="!showDeleteConfirm && !showTtlEdit" class="bulk-toolbar">
        <div class="toolbar-content">
          <div class="selection-info">
            <v-icon icon="mdi-check-circle" size="20" />
            <span>{{ t('dashboard.domains.dns.bulkSelected', { count: selectedCount }) }}</span>
          </div>

          <div class="toolbar-actions">
            <button class="toolbar-btn" @click="openTtlEdit">
              <v-icon icon="mdi-clock-outline" size="18" />
              <span>{{ t('dashboard.domains.dns.bulkChangeTtl') }}</span>
            </button>
            <button class="toolbar-btn danger" @click="confirmDelete">
              <v-icon icon="mdi-trash-can-outline" size="18" />
              <span>{{ t('dashboard.domains.dns.delete') }}</span>
            </button>
          </div>

          <button class="close-btn" :title="t('dashboard.domains.dns.bulkClearSelection')" @click="emit('clearSelection')">
            <v-icon icon="mdi-close" size="20" />
          </button>
        </div>
      </div>
    </Transition>

    <!-- Delete Confirmation -->
    <Transition name="slide-up">
      <div v-if="showDeleteConfirm" class="bulk-toolbar confirm-mode">
        <div class="toolbar-content">
          <div class="confirm-message">
            <v-icon icon="mdi-alert-circle" size="22" color="error" />
            <span>{{ t('dashboard.domains.dns.bulkDeleteConfirm', { count: selectedCount }) }}</span>
          </div>

          <div v-if="deleting" class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
            </div>
            <span class="progress-text">{{ deleteProgress.current }}/{{ deleteProgress.total }}</span>
          </div>

          <div v-else class="toolbar-actions">
            <button class="toolbar-btn" @click="cancelDelete">
              {{ t('common.cancel') }}
            </button>
            <button class="toolbar-btn danger" @click="executeDelete">
              <v-icon icon="mdi-trash-can" size="18" />
              <span>{{ t('dashboard.domains.dns.delete') }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- TTL Edit -->
    <Transition name="slide-up">
      <div v-if="showTtlEdit" class="bulk-toolbar edit-mode">
        <div class="toolbar-content">
          <div class="edit-label">
            <v-icon icon="mdi-clock-outline" size="20" />
            <span>{{ t('dashboard.domains.dns.bulkTtlLabel', { count: selectedCount }) }}</span>
          </div>

          <div v-if="updating" class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill success" :style="{ width: updateProgressPercent + '%' }" />
            </div>
            <span class="progress-text">{{ updateProgress.current }}/{{ updateProgress.total }}</span>
          </div>

          <div v-else class="ttl-select-wrapper">
            <select v-model="selectedTtl" class="ttl-select">
              <option v-for="preset in translatedTtlPresets" :key="preset.value" :value="preset.value">
                {{ preset.label }}
              </option>
            </select>
          </div>

          <div v-if="!updating" class="toolbar-actions">
            <button class="toolbar-btn" @click="cancelTtlEdit">
              {{ t('common.cancel') }}
            </button>
            <button class="toolbar-btn primary" @click="executeTtlUpdate">
              <v-icon icon="mdi-check" size="18" />
              <span>{{ t('dashboard.domains.dns.bulkApply') }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.bulk-toolbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  min-width: 420px;
  max-width: 90vw;
}

.toolbar-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 14px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(0, 176, 255, 0.1);
}

.confirm-mode .toolbar-content {
  border-color: rgba(244, 67, 54, 0.35);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(244, 67, 54, 0.15);
}

.edit-mode .toolbar-content {
  border-color: rgba(0, 176, 255, 0.35);
}

.selection-info,
.confirm-message,
.edit-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}

.confirm-message {
  color: #ef5350;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.9);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(120, 163, 255, 0.18);
    border-color: rgba(120, 163, 255, 0.35);
  }

  &.danger {
    background: rgba(244, 67, 54, 0.12);
    border-color: rgba(244, 67, 54, 0.25);
    color: #ef5350;

    &:hover {
      background: rgba(244, 67, 54, 0.2);
      border-color: rgba(244, 67, 54, 0.4);
    }
  }

  &.primary {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00b0ff;

    &:hover {
      background: rgba(0, 176, 255, 0.25);
      border-color: rgba(0, 176, 255, 0.45);
    }
  }
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;
  margin-left: 4px;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 150px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(120, 163, 255, 0.15);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ef5350, #f44336);
  border-radius: 3px;
  transition: width 0.2s ease;

  &.success {
    background: linear-gradient(90deg, #00b0ff, #1fe2d4);
  }
}

.progress-text {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
  font-weight: 500;
  white-space: nowrap;
}

.ttl-select-wrapper {
  flex-shrink: 0;
}

.ttl-select {
  padding: 8px 32px 8px 12px;
  background: rgba(15, 23, 48, 0.8);
  border: 1px solid rgba(0, 176, 255, 0.3);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%2300b0ff' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:focus {
    outline: none;
    border-color: rgba(0, 176, 255, 0.5);
  }

  option {
    background: #0f1730;
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

/* Light Theme */
.v-theme--lightTheme {
  .toolbar-content {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(28, 110, 227, 0.25);
  }

  .selection-info,
  .edit-label {
    color: rgba(15, 23, 42, 0.9);
  }

  .toolbar-btn {
    color: rgba(15, 23, 42, 0.85);
  }

  .ttl-select {
    background: rgba(255, 255, 255, 0.9);
    color: rgba(15, 23, 42, 0.9);

    option {
      background: #ffffff;
      color: rgba(15, 23, 42, 0.9);
    }
  }
}

/* Responsive */
@media (max-width: 600px) {
  .bulk-toolbar {
    bottom: 16px;
    left: 16px;
    right: 16px;
    transform: none;
    min-width: auto;
    max-width: none;
  }

  .toolbar-content {
    flex-wrap: wrap;
    gap: 12px;
  }

  .toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(20px);
  }
}
</style>
